import React, { useContext, useEffect, useRef } from "react"
import { Animated, Easing, StyleSheet, TouchableOpacity } from "react-native"
import { SelectedIcon as ISelectedIcon } from "@ecountabl/lib"
import { ThemeContext } from "../../context/ThemeContext"
import { SelectedIcon } from "../../selected-icon"

type Props = {
  selected: boolean
  onPress: () => void
  disabled?: boolean
  color?: string
  icon?: ISelectedIcon
  accessibilityLabel: string
}

const isHexColor = (input?: string): boolean => /^#[0-9a-fA-F]{6}$/.test(input || "")

const withAlpha = (hex: string | undefined, alpha: number, fallback: string): string => {
  if (!hex || !isHexColor(hex)) return fallback
  const clean = hex.replace("#", "")
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const ThemedIconSwitch: React.FC<Props> = ({
  selected,
  onPress,
  disabled = false,
  color,
  icon,
  accessibilityLabel,
}: Props) => {
  const theme = useContext(ThemeContext)
  const { primary, disabled: disabledColor, placeholder } = theme.theme.colors
  const accent = isHexColor(color) ? color : primary
  const hasIcon = !!icon?.family
  const progress = useRef(new Animated.Value(selected ? 1 : 0)).current

  useEffect(() => {
    Animated.timing(progress, {
      toValue: selected ? 1 : 0,
      duration: 320,
      easing: Easing.bezier(0.2, 0.8, 0.2, 1),
      useNativeDriver: true,
    }).start()
  }, [selected])

  const translateX = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 30],
  })
  const rotate = progress.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "360deg"],
  })

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      hitSlop={10}
      accessibilityRole="switch"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ checked: selected, disabled }}
      style={[
        styles.track,
        {
          backgroundColor: selected
            ? withAlpha(accent, 0.18, "rgba(148,163,184,0.14)")
            : "transparent",
          borderColor: selected
            ? withAlpha(accent, 0.45, disabledColor || placeholder)
            : (disabledColor || placeholder),
          opacity: disabled ? 0.7 : 1,
        },
      ]}
    >
      <Animated.View
        style={[
          styles.thumb,
          {
            backgroundColor: selected ? accent : "#ffffff",
            opacity: selected ? 1 : 0.55,
            borderColor: selected
              ? withAlpha(accent, 0.45, disabledColor || placeholder)
              : (disabledColor || placeholder),
            transform: hasIcon
              ? [{ translateX }, { rotate }]
              : [{ translateX }],
          },
        ]}
      >
        {hasIcon ? (
          <SelectedIcon
            icon={icon as ISelectedIcon}
            color={selected ? "#FFFFFF" : (disabledColor || placeholder)}
            size={14}
          />
        ) : null}
      </Animated.View>
    </TouchableOpacity>
  )
}

const styles = StyleSheet.create({
  track: {
    width: 60,
    height: 30,
    borderRadius: 30,
    borderWidth: 1,
    overflow: "hidden",
    position: "relative",
  },
  thumb: {
    width: 24,
    height: 24,
    borderRadius: 24,
    borderWidth: 1,
    position: "absolute",
    left: 3,
    top: 2,
    alignItems: "center",
    justifyContent: "center",
  },
})

export default ThemedIconSwitch
